/* eslint-disable prettier/prettier */
import { EDITOR_QUERY_NAME } from 'components/GridCard/constants';

import type {
  GenerateCategories,
  Hashtag,
  WebEditor,
  Explore,
  FollowArtist,
  FollowTag,
} from 'utils/types';

import {
  SINGLE_VIEW_SLIDERS_LIMIT,
  SINGLE_VIEW_SIMILAR_LIMIT,
  SINGLE_VIEW_LIMIT,
  IMAGE_TYPES,
} from './main';

const API_URL = process.env.NEXT_PUBLIC_API_URL;

const generateCategories: GenerateCategories = url => ({
  images: `${url}&type=photo`,
  gifs: `${url}&type=photo&ext=gif`,
  stickers: `${url}&type=sticker`,
});

const hashtag: Hashtag = Object.freeze({
  popular: generateCategories(`${API_URL}/photos/search.json?sort=recently_most_edited`),
  'most-edited': generateCategories(`${API_URL}/photos/search.json?sort=fte_large`),
});

const webEditor: WebEditor = {
  createExternal: imageUrl => `https://picsart.com/create/editor?image=${imageUrl}`,
  createInternal: (imageId, type = 'imageId') =>
    `/create/editor?${type}=${imageId}${
      type === EDITOR_QUERY_NAME.template ? '&category=templates' : ''
    }`,
  createAILink: ({ caption, styles = '' }) =>
    `/create/editor?category=templates&app=t2i&prompt=${encodeURIComponent(caption)}${
      styles ? `&styles=${encodeURIComponent(styles)}` : ''
    }`,
};

const explore: Explore = Object.freeze({
  'popular-images': '/photos/show/featured.json',
  'image-picks': '/photos/show/featured.json',
  'free-to-edit-images': '/photos/search.json?tag=freetoedit',
});

const MAIN_DOMAIN = process.env.NEXT_PUBLIC_MAIN_DOMAIN || 'https://picsart.com';

// !! TODO: MOVE HOSTS TO ENV VARIABLES
const IMAGE_VIEW_URL = `${MAIN_DOMAIN}/i`;

const USER_PROFILE_URL = `${MAIN_DOMAIN}/u`;

const ADD_PHOTO = `${API_URL}/photos/add.json`;

const FEED_URL = `${API_URL}/photos/following/show/me.json`;

const getArtistFollowingEndpoint = ({ action, artistId }: FollowArtist) =>
  `${API_URL}/following/${action}/${artistId}.json`;

const getTagFollowingEndpoint = ({ action }: FollowTag) => `${API_URL}/users/tags/${action}.json`;

const search = (category, query) => {
  const endpoints = Object.freeze({
    all: {
      withQuery: `${API_URL}/search/cards.json?market=website&type=all&include_photos=1&accessToken=&q=${query}`,
      withoutQuery: `${API_URL}/search/cards.json?market=website&type=all&include_photos=1&accessToken=`,
    },
    images: {
      withQuery: `${API_URL}/photos/search.json?offset=0&q=${query}`,
      withoutQuery: `${API_URL}/search/cards.json?market=website&type=photos&offset=0`,
    },
    artists: {
      withQuery: `${API_URL}/users/search.json?offset=0&include_photos=1&market=website&q=${query}`,
      withoutQuery: `${API_URL}/search/cards.json?type=artists&include_photos=1&market=website&offset=0`,
    },
    tags: {
      withQuery: `${API_URL}/tags/suggestions.json?offset=0&q=${query}`,
      withoutQuery: `${API_URL}/tags/suggestions.json?offset=0`,
    },
    stickers: {
      withQuery: `${API_URL}/photos/search.json?type=sticker&offset=0&q=${query}`,
      withoutQuery: `${API_URL}/stickers/search.json?type=sticker&animated_support=0&offset=0&market=website&accessToken=`,
    },
    quick: {
      withQuery: `${API_URL}/tags/suggestions.json?offset=0&market=website&q=${query}`,
      withoutQuery: `${API_URL}/tags/suggestions.json?offset=0&market=website`,
    },
  });

  if (query) {
    return endpoints[category]?.withQuery;
  }
  return endpoints[category]?.withoutQuery;
};

const PHOTO_LIKE = {
  /**
   * @method values<add|remove>
   */
  getLikePhotoEndpoint(id: number, method = 'add') {
    return `${API_URL}/photos/likes/${method}/${id}.json`;
  },
};

const SINGLE_VIEW_ENDPOINTS = {
  getCollections(imageId: number, offset?: number, limit?: number) {
    const requestLimit = limit || SINGLE_VIEW_SLIDERS_LIMIT;
    return `${API_URL}/photos/streams/show/${imageId}.json?offset=${offset}&limit=${requestLimit}`;
  },
  getImageDetailed(imageId: number, accessToken: string) {
    return `${API_URL}/image/show/${imageId}?accessToken=${accessToken}`;
  },
  getNextImage(accessToken, limit?: number, offset?: number) {
    const requestLimit = limit === 0 ? 0 : limit || SINGLE_VIEW_LIMIT;
    const requestOffset = offset || 0;
    return `${API_URL}/photos/show/featured.json?offset=${requestOffset}&limit=${requestLimit}&accessToken=${accessToken}`;
  },
  getRelevantCategories(imageId: number) {
    return `${API_URL}/photos/${imageId}/image-categories`;
  },
  getRemixes(imageId: number, limit?: number, offset?: number) {
    const requestLimit = limit || SINGLE_VIEW_SIMILAR_LIMIT;
    const requestOffset = offset ? `&offset=${offset}` : '';
    return `${API_URL}/photos/forks/show/${imageId}.json?sticker_support=true&limit=${requestLimit}${requestOffset}&include_user=true`;
  },
  getSimilarImages(
    imageType: IMAGE_TYPES,
    imageId: number,
    accessToken?: string,
    limit?: number,
    offset?: number,
  ) {
    const requestLimit = limit || SINGLE_VIEW_SIMILAR_LIMIT;
    const requestOffset = offset ? `&offset=${offset}` : '';
    return `${API_URL}/${imageType}s/show/similars/${imageId}.json?limit=${requestLimit}${requestOffset}&accessToken=${accessToken}`;
  },
  getSources(imageId: number) {
    return `${API_URL}/photos/sources/show/${imageId}.json`;
  },
  getDeleteEndpoint(id, accessToken) {
    return `${API_URL}/photos/remove/${id}.json?accessToken=${accessToken}`;
  },
  getEditImageEndpoint(id, accessToken) {
    return `${API_URL}/photos/update/${id}.json?accessToken=${accessToken}`;
  },
  getReportEndpoint(type, accessToken) {
    const endpoints = {
      hashtag: `${API_URL}/hashtag/reports/add/${accessToken}`,
      image: `${API_URL}/photos/reports/add/${accessToken}.json`,
    };
    return endpoints[type];
  },
  getEXIF(imageId: number) {
    return `${API_URL}/photos/exif?photoId=${imageId}`;
  },
};

const COLLECTIONS_ENDPOINTS = {
  delete(item_id: number) {
    return `${API_URL}/collections/items/${item_id}`;
  },
  removeFromOne(item_id: string, collection_id: string) {
    return `${API_URL}/collections/${collection_id}/items/${item_id}`;
  },

  get(photoId: string | string[]) {
    return `${API_URL}/collections?item_id=${photoId}`;
  },
  create() {
    return `${API_URL}/collections`;
  },
  save() {
    return `${API_URL}/collections/items`;
  },
  check(photo_id: string) {
    return `${API_URL}/photos/${photo_id}/collections`;
  },
};

const getImageLikesEndpoint = photoId => `${API_URL}/photos/likes/show/${photoId}.json`;

const FORGOT_PASSWORD_ENDPOINTS = {
  resetEmail: `${API_URL}/user-account/reset.json`,
};

const NAV_LINKS_ENDPOINT = `${API_URL}/growth-utilities/v1/navigationLinks/groups/filter`;

export {
  search,
  API_URL,
  hashtag,
  explore,
  webEditor,
  MAIN_DOMAIN,
  FEED_URL,
  ADD_PHOTO,
  PHOTO_LIKE,
  IMAGE_VIEW_URL,
  USER_PROFILE_URL,
  SINGLE_VIEW_ENDPOINTS,
  COLLECTIONS_ENDPOINTS,
  getImageLikesEndpoint,
  getTagFollowingEndpoint,
  getArtistFollowingEndpoint,
  FORGOT_PASSWORD_ENDPOINTS,
  NAV_LINKS_ENDPOINT,
};
